import { ARENA } from '../../config/constants.js';

const HOLD_RANGE = 130; // stops approaching once this close, holds position and fires instead
const TELEGRAPH_DURATION = 0.35; // brief windup before each shot, same telegraph-everything language as Charger's dash
const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08;

/**
 * A minion that stops at range instead of closing to melee, telegraphs, then
 * fires a slow aimed shot — the first minion that's a stationary zone threat
 * rather than a chaser, forcing the player to either close in and kill it or
 * route around its lane. Resumes approaching if the player moves out of
 * HOLD_RANGE. Same public shape as Skirmisher (x/y/radius/hp/isDead/
 * takeDamage/update) plus an `isTelegraphing` getter (see Charger) so
 * Game.js's existing white hit-flash override picks up its windup for free.
 */
export class Caster {
  constructor(eventBus, {
    x, y, radius = 6, hp, moveSpeed, contactDamage, shotDamage, shotSpeed, fireInterval,
  }) {
    this.eventBus = eventBus;
    this.type = 'caster';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.shotDamage = shotDamage;
    this.shotSpeed = shotSpeed;
    this._fireInterval = fireInterval;
    this._fireTimer = fireInterval * Math.random();
    this._contactCooldown = 0;
    this.isDead = false;
    this.hitFlashTimer = 0;

    this._telegraphTimer = 0;
    this._aimAngle = 0;
  }

  get isTelegraphing() {
    return this._telegraphTimer > 0;
  }

  update(dt, player, bulletPool) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;

    if (dist > HOLD_RANGE) {
      this.x += (dx / dist) * this.moveSpeed * dt;
      this.y += (dy / dist) * this.moveSpeed * dt;
      this._clampToArena();
    }

    if (this._telegraphTimer > 0) {
      this._telegraphTimer -= dt;
      if (this._telegraphTimer <= 0) {
        bulletPool.fire({
          x: this.x,
          y: this.y,
          vx: Math.cos(this._aimAngle) * this.shotSpeed,
          vy: Math.sin(this._aimAngle) * this.shotSpeed,
          radius: 3,
          damage: this.shotDamage,
          color: '#7a4ae0',
          lifetime: 5,
        });
        this._fireTimer = this._fireInterval;
      }
    } else {
      this._fireTimer -= dt;
      if (this._fireTimer <= 0) {
        this._aimAngle = Math.atan2(dy, dx); // locked in at telegraph start, same as Charger's charge direction
        this._telegraphTimer = TELEGRAPH_DURATION;
      }
    }

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    const appliedDamage = hpBefore - this.hp;
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: appliedDamage, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
